<template>
  <div class="stock-po-movements stock-linked-section">
    <v-subheader class="stock-linked-section__title px-0 font-weight-bold">
      {{ $t("STOCK.PURCHASE_ORDERS.RELATED_MOVEMENTS") }}
    </v-subheader>
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
    <v-data-table
      v-if="purchaseOrderId"
      :headers="headers"
      :items="items"
      :loading="loading"
      hide-default-footer
      :items-per-page="-1"
      class="stock-po-movements__table stock-linked-table"
    >
      <template v-slot:item.code="{ item }">
        <router-link :to="{ name: 'stock-receipts-view', params: { id: item.id } }">
          {{ item.code }}
        </router-link>
        <div v-if="item.parent_action" class="caption grey--text text--darken-1">
          {{ $t("STOCK.RECEIPTS.PARENT_DOCUMENT") }}:
          <router-link
            :to="{ name: 'stock-receipts-view', params: { id: item.parent_action.id } }"
          >
            {{ item.parent_action.code }}
          </router-link>
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="receiptStatusColor(item.status)" dark>
          {{ receiptStatusLabel(item.status) }}
        </v-chip>
      </template>
      <template v-slot:item.counterparty="{ item }">
        {{ counterpartyLabel(item) }}
      </template>
      <template v-slot:item.warehouse="{ item }">
        {{ warehouseLabel(item) }}
      </template>
      <template v-slot:item.action_date="{ item }">
        {{ item.action_date || "-" }}
      </template>
      <template v-slot:item.items_count="{ item }">
        {{ item.items_count != null ? item.items_count : "-" }}
      </template>
      <template v-slot:item.total_gross="{ item }">
        {{ formatMovementMoney(item.total_gross, item.currency_code) }}
      </template>
      <template v-slot:item.total_inventory_cost_net="{ item }">
        {{ formatFinancialValue(item.total_inventory_cost_net, item.currency_code) }}
      </template>
      <template v-slot:item.transport_order_code="{ item }">
        <router-link
          v-if="item.transport_order_id"
          :to="{ name: 'stock-transport-orders-view', params: { id: item.transport_order_id } }"
        >
          {{ item.transport_order_code }}
        </router-link>
        <span v-else>-</span>
      </template>
    </v-data-table>
    <v-alert v-if="!loading && !items.length" type="info" dense text class="mt-2">
      {{ $t("STOCK.PURCHASE_ORDERS.NO_RELATED_MOVEMENTS") }}
    </v-alert>
  </div>
</template>

<script>
import stockMovementRowDisplayMixin from "@/views/partials/stock/stockMovementRowDisplay.mixin";
import {
  buildLinkedDocumentHeaders,
  linkedReceiptFinancialHeaders,
  linkedTransportOrderReferenceHeader,
} from "@/views/partials/stock/stockLinkedTableHeaders";
import { GET_STOCK_PO_MOVEMENTS } from "@/store/stock/stockPurchaseOrders.module";

export default {
  name: "StockPurchaseOrderMovementsTable",
  mixins: [stockMovementRowDisplayMixin],
  props: {
    purchaseOrderId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
    };
  },
  computed: {
    headers() {
      return [
        ...buildLinkedDocumentHeaders(this.$t.bind(this), {
          counterpartyLabelKey: "STOCK.RECEIPTS.SUPPLIER",
          totalLabelKey: "STOCK.PURCHASE_ORDERS.TOTAL_GROSS",
        }),
        ...linkedReceiptFinancialHeaders(this.$t.bind(this)),
        linkedTransportOrderReferenceHeader(this.$t.bind(this)),
      ];
    },
  },
  watch: {
    purchaseOrderId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.reload();
        } else {
          this.items = [];
        }
      },
    },
  },
  methods: {
    reload() {
      if (!this.purchaseOrderId) {
        return Promise.resolve();
      }

      this.loading = true;
      return this.$store
        .dispatch(GET_STOCK_PO_MOVEMENTS, {
          purchaseOrderId: this.purchaseOrderId,
          itemsPerPage: 100,
        })
        .then((response) => {
          this.items = response.data || [];
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/views/partials/stock/stockLinkedTable.scss";

::v-deep .stock-po-movements__table th {
  white-space: nowrap;
}

.stock-po-movements__table a {
  text-decoration: none;
  font-weight: 500;
}
.stock-po-movements__table a:hover {
  text-decoration: underline;
}
</style>
